import {useMemo} from 'react';
import {kr, tickLabels} from '../../lib/format.js';
import {Defs, fillById, niceScale, Grid, useHover, HitBands, Tooltip, Legend} from './primitives.jsx';

const W = 1160, H = 420, L = 68, R = 12, T = 14, B = 26;

/**
 * The whole liquidity position in one picture: cash and receivables above the line,
 * payables and the VAT debt below it, and the net of all four as a line across.
 *
 * The VAT band is the reason this chart exists. Cash alone flatters, because a bank
 * balance includes VAT that has been collected from customers and not yet handed to
 * Skatteverket — 83 630 kr of the balance on this data is somebody else's money. Reported
 * quarterly, that debt is paid in one lump on the 12th of the second month after the
 * quarter closes, which is what gives the band its sawtooth: it builds for three months
 * and is cleared in one. A runway read off cash alone misses those steps entirely.
 *
 * The four are STACKED rather than netted so the composition stays visible. A single net
 * line would hide that a comfortable-looking position is mostly an unpaid receivable, and
 * hide the VAT step that is about to remove a quarter of the cash.
 */
const UP = [
	{key: 'cash', label: 'Kassa', fill: 'solid'},
	{key: 'receivable_open', label: 'Kundfordringar', fill: 'open'},
];
const DOWN = [
	{key: 'payable_open', label: 'Leverantörsskulder', fill: 'cross'},
	{key: 'vat', label: 'Momsskuld att betala', fill: 'dots'},
];

export default function LiquidityPosition({rows, vatPayments = []}) {
	const {at, enter, leave} = useHover();
	const ticks = useMemo(() => tickLabels(rows), [rows]);
	const g = useMemo(() => {
		// Down-series arrive in the ledger's own sign (liabilities negative), so the
		// stack simply adds them and the axis holds both directions.
		const hi = Math.max(0, ...rows.map(r => UP.reduce((a, s) => a + (r[s.key] ?? 0), 0)));
		const lo = Math.min(0, ...rows.map(r => DOWN.reduce((a, s) => a + (r[s.key] ?? 0), 0)));
		const sc = niceScale(lo, hi);
		const iw = W - L - R, ih = H - T - B;
		return {
			sc,
			y: v => T + ih - ((v - sc.lo) / (sc.hi - sc.lo)) * ih,
			x: i => L + (iw * (i + 0.5)) / rows.length,
			bw: Math.max(3, Math.min(30, (iw / rows.length) * 0.66)),
		};
	}, [rows]);

	if (!rows.length) return null;

	const net = r => UP.concat(DOWN).reduce((a, s) => a + (r[s.key] ?? 0), 0);
	const lastBooked = rows.findLastIndex(r => !r.projected);
	const boundary = lastBooked >= 0 && lastBooked < rows.length - 1
		? (g.x(lastBooked) + g.x(lastBooked + 1)) / 2 : null;
	const payMonths = new Set(vatPayments.filter(p => p.amount > 0).map(p => p.month));

	const stack = (r, i, series) => {
		let run = 0;
		return series.map(s => {
			const v = r[s.key] ?? 0;
			if (!v) return null;
			const from = g.y(run), to = g.y(run + v);
			run += v;
			const h = Math.abs(to - from);
			if (h < 0.4) return null;
			return <rect key={`${r.month}-${s.key}`} x={g.x(i) - g.bw / 2} y={Math.min(from, to)}
				width={g.bw} height={Math.max(h, 0.6)} rx={g.bw > 6 ? 2 : 0} {...fillById(s.fill)} />;
		});
	};

	return (
		<div className="chart">
			<div className="chart__head">
				<span className="chart__title">Likviditetsposition: kassa, kundfordringar, leverantörsskulder och moms</span>
				<span className="chart__hint">
					utgående ställning per månad. Momsskulden betalas kvartalsvis — den 12:e i andra
					månaden efter kvartalets slut — vilket är sågtanden i det nedre bandet.
				</span>
			</div>
			<div className="chart__canvas chart__canvas--wide">
				<svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet" role="img"
					aria-label="Kassa, kundfordringar, leverantörsskulder och momsskuld per månad">
					<Defs />
					<Grid {...g.sc} y={g.y} L={L} R={R} W={W} />
					{boundary != null && (
						<g>
							<line x1={boundary} x2={boundary} y1={T} y2={H - B} stroke="#8a8a8a"
								strokeWidth={1} strokeDasharray="3 3" />
							<text x={boundary + 4} y={T + 10} className="ax" textAnchor="start">prognos →</text>
						</g>
					)}
					{/* A quarter's VAT settlement is a date, not a shape — marked on the axis so
					    the step in the band can be named. */}
					{rows.map((r, i) => (payMonths.has(r.month) ? (
						<line key={`vp-${r.month}`} x1={g.x(i)} x2={g.x(i)} y1={T} y2={H - B}
							stroke="#c4c4c4" strokeWidth={g.bw + 4} opacity={0.35} />
					) : null))}
					{rows.map((r, i) => stack(r, i, UP))}
					{rows.map((r, i) => stack(r, i, DOWN))}
					<line x1={L} x2={W - R} y1={g.y(0)} y2={g.y(0)} stroke="#000" strokeWidth={1.2} />
					<polyline fill="none" stroke="#000" strokeWidth={2.2} strokeLinejoin="round"
						points={rows.map((r, i) => `${g.x(i)},${g.y(net(r))}`).join(' ')} />
					{rows.map((r, i) => (
						<circle key={`n-${r.month}`} cx={g.x(i)} cy={g.y(net(r))} r={2.8}
							fill="#fff" stroke="#000" strokeWidth={1.6} />
					))}
					{ticks.map((t, i) => (t == null ? null : (
						<text key={rows[i].month} x={g.x(i)} y={H - 8} className="axs" textAnchor="middle">{t}</text>
					)))}
					<HitBands months={rows} x={g.x} W={W} H={H} onEnter={enter} onLeave={leave} />
				</svg>
				<Tooltip months={rows} at={at} x={g.x} W={W}
					title={m => `${m.label ?? m.month}${m.projected ? ' · prognos' : ''}`}
					rows={m => [
						['Kassa', `${kr(m.cash)} kr`],
						['Kundfordringar', `${kr(m.receivable_open)} kr`],
						['Leverantörsskulder', `${kr(m.payable_open)} kr`],
						['Momsskuld', `${kr(m.vat)} kr`],
						...(payMonths.has(m.month)
							? [['Momsbetalning denna månad', `−${kr(m.vat_out)} kr`]] : []),
						['Nettoposition', `${kr(net(m))} kr`],
					]} />
			</div>
			<Legend items={[...UP, ...DOWN].map(s => ({label: s.label, fill: s.fill}))
				.concat([{label: 'Nettoposition', dash: null}])} />
		</div>
	);
}
